import type { DividerPolicy } from "@CommonTypes/backend/PspRules";
import type { PogNonCircular } from "@CommonTypes/merchflow/pog/pog";
import type { LoaderLibraryShelf } from "@CommonUtils/pog/dividerPolicy";
import {
  TELL_O_DEFAULT_POLICY,
  applyTellOProposal,
  buildTellOChips,
  describeTellOProposal,
  normalizeTellOText,
  parseTellOText,
  proposeTellOShelf,
  type RuleProposal,
  type TellOChip,
  type TellOContext,
  type TellOReply,
  type TellOShelfProposal,
  type TellOUnit,
  type TellOTranslate,
} from "@CommonUtils/pog/tellO";
import type {
  DividerLevelNames,
  DividerRulesProduct,
  FixtureDividerRows,
} from "../dividerRulesModel";
import { loaderRulesState, withPrefilledLoaderDepth } from "../loaderRulesModel";

export interface TellOWindowData {
  basePog: PogNonCircular | null;
  products: readonly DividerRulesProduct[];
  unit: string;
  levelNames?: DividerLevelNames;
  loaderLibraryShelves: readonly LoaderLibraryShelf[];
  /** Shelf numbers here use the base planogram’s stored order, not top-first request order. */
  fixtureDividerRows?: FixtureDividerRows;

  baseStoreCode?: string | null;
}

export const tellOUnit = (unit: string): TellOUnit => (unit === "cm" ? "cm" : "in");

type ShelfKey = `${number}:${number}`;
const shelfKey = (bayNo: number, shelfNo: number): ShelfKey => `${bayNo}:${shelfNo}`;

// Fixtures use stored shelf order; requests count from the top, with stored order breaking notch ties.
const shelfNumbering = (basePog: PogNonCircular | null) => {
  const fromTop = new Map<ShelfKey, number>();
  const stored = new Map<ShelfKey, number>();
  (basePog?.planogram?.bays ?? []).forEach((bay, bayIndex) => {
    (bay.shelves ?? [])
      .map((shelf, index) => ({ notch: Number(shelf.notchNo) || 0, index }))
      .sort((a, b) => b.notch - a.notch || a.index - b.index)
      .forEach(({ index }, position) => {
        fromTop.set(shelfKey(bayIndex + 1, index + 1), position + 1);
        stored.set(shelfKey(bayIndex + 1, position + 1), index + 1);
      });
  });
  return { fromTop, stored };
};

export const fixtureRowsFromTop = (
  rows: FixtureDividerRows,
  basePog: PogNonCircular | null,
): FixtureDividerRows => {
  if (!rows) return rows;
  const { fromTop } = shelfNumbering(basePog);
  return rows.flatMap((row) => {
    const shelfNo = fromTop.get(shelfKey(row.bayNo, row.shelfNo));
    return shelfNo === undefined ? [] : [{ ...row, shelfNo }];
  });
};

export const tellOContext = (policy: DividerPolicy, data: TellOWindowData): TellOContext => ({
  policy,
  products: data.products,
  levelNames: data.levelNames,
  basePog: data.basePog,
  libraryShelves: data.loaderLibraryShelves,

  fixtureRows: fixtureRowsFromTop(data.fixtureDividerRows, data.basePog) ?? null,
  unit: tellOUnit(data.unit),
  baseStoreCode: data.baseStoreCode ?? null,
});

export const isDefaultPolicy = (policy: DividerPolicy) =>
  policy.source === "base" &&
  !policy.keepFixtureDividers &&
  (policy.loaderMode ?? "products") === "products" &&
  !(policy.hardware?.width ?? 0) &&
  !(policy.loaderDepth ?? 0);

export const DEFAULT_POLICY: DividerPolicy = TELL_O_DEFAULT_POLICY;

export const isUndoLast = (text: string) =>
  /^undo(?: that| it| this)?$/.test(normalizeTellOText(text));

export const tellOChips = (
  context: TellOContext,
  canUndo: boolean,
  t?: TellOTranslate,
): TellOChip[] => buildTellOChips(context, { afterApply: canUndo }, t);

export const readTellOText = (context: TellOContext, text: string): RuleProposal | null =>
  parseTellOText(context, text);

export const describeReply = (
  context: TellOContext,
  proposal: RuleProposal,
  t?: TellOTranslate,
): TellOReply => describeTellOProposal(context, proposal, t);

export const applyProposal = (
  policy: DividerPolicy,
  proposal: RuleProposal,
  data: TellOWindowData,
): DividerPolicy => {
  const next = applyTellOProposal(policy, proposal);
  if (next.source !== "items") return next;
  const { evidence } = loaderRulesState(
    next,
    data.products,
    data.basePog,
    data.loaderLibraryShelves,
  );
  return withPrefilledLoaderDepth(next, evidence);
};

const sameShelf = (row: { bayNo: number; shelfNo: number }, proposal: TellOShelfProposal) =>
  row.bayNo === proposal.bayNo && row.shelfNo === proposal.shelfNo;

// Convert a saved top-first shelf number back to stored order before updating fixture rows.
export const fixtureRowsAsSaved = (
  rows: FixtureDividerRows,
  saved: readonly TellOShelfProposal[],
  basePog: PogNonCircular | null,
): FixtureDividerRows => {
  if (!rows) return rows;
  const { stored } = shelfNumbering(basePog);
  return saved.reduce((current, proposal) => {
    if (!proposal.isBaseStore || proposal.feature !== "dividers") return current;
    const shelfNo = stored.get(shelfKey(proposal.bayNo, proposal.shelfNo));
    if (shelfNo === undefined) return current;
    return current.map((row) =>
      row.bayNo === proposal.bayNo && row.shelfNo === shelfNo
        ? { ...row, dividersSetByHand: proposal.dividersSetByHand }
        : row,
    );
  }, rows);
};

// Another store has no known previous value here, so offering Undo would clear an unknown override.
export const isOtherStoreShelf = (context: TellOContext, proposal: RuleProposal) =>
  proposal.kind === "shelf" &&
  (!proposal.isBaseStore ||
    (context.baseStoreCode !== null && proposal.storeCode !== context.baseStoreCode));

export const withWindowPrevious = (
  context: TellOContext,
  proposal: TellOShelfProposal,
): TellOShelfProposal => {
  if (!proposal.isBaseStore || proposal.feature !== "dividers" || !context.fixtureRows) {
    return proposal;
  }
  const again = proposeTellOShelf(
    { ...context, baseStoreCode: proposal.storeCode },
    {
      bayNo: proposal.bayNo,
      shelfNo: proposal.shelfNo,
      storeCode: proposal.storeCode,
      feature: "dividers",
      dividersSetByHand: proposal.dividersSetByHand,
    },
  );
  if (again.kind !== "shelf" || again.feature !== "dividers") return proposal;
  const { previous: _previous, ...rest } = proposal;
  return again.previous === undefined ? rest : { ...rest, previous: again.previous };
};

export const libraryShelvesAsSaved = (
  shelves: readonly LoaderLibraryShelf[],
  saved: readonly TellOShelfProposal[],
): readonly LoaderLibraryShelf[] =>
  saved.reduce((current, proposal) => {
    if (!proposal.isBaseStore || proposal.feature !== "loaders") return current;
    const shelf = {
      bayNo: proposal.bayNo,
      shelfNo: proposal.shelfNo,
      loaderDepth: proposal.loaderDepth,
    };
    return current.some((row) => sameShelf(row, proposal))
      ? current.map((row) =>
          sameShelf(row, proposal) ? { ...row, loaderDepth: shelf.loaderDepth } : row,
        )
      : [...current, shelf];
  }, shelves);
